import { DonutChart, Heading } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'
import { useTranslation } from 'react-i18next'
import type { TodayMonitoring } from 'screens/Rostering/types'
import styled from 'styled-components'
import DonutChartTooltip from './DonutChartTooltip'
import EventCount from './EventCount'

export enum StatusColors {
  DRAFT = '#999999',
  PENDING = '#9ACEFF',
  DUE = '#1890FF',
  IN_PROGRESS = '#9966FB',
  OVERDUE = '#E01E4E',
  DONE = '#37A598',
  CANCELLED = '#595959'
}

type StatusWidgetProps = {
  todayMonitoring: TodayMonitoring
}

const StatusWidget = ({ todayMonitoring }: StatusWidgetProps) => {
  const { t } = useTranslation()

  if (!todayMonitoring.statusSummary) return null

  return (
    <Wrapper>
      <Heading level={3}>{t('STATUS')}</Heading>
      <StyledDivider />
      <ContentContainer>
        <EventsContainer>
          <LightText level={2}>{t('EVENTS')}</LightText>
          {Object.entries(todayMonitoring.statusSummary.statuses).map(
            ([key, value]) => (
              <EventCount type={key} count={value as number} />
            )
          )}
        </EventsContainer>
        <ChartContainer>
          <Chart>
            <DonutChart
              data={Object.entries(todayMonitoring.statusSummary.statuses).map(
                ([key, value]) => ({
                  name: key as string,
                  y: value as number
                })
              )}
              colors={Object.values(StatusColors)}
              displaySubtitle={false}
              displayLabels={false}
              slicing
              fitContainer
              size={220}
              customTooltip={(type, value) => (
                <DonutChartTooltip type={type} value={value} />
              )}
            />
          </Chart>
          <Description>
            <Heading level={1}>{todayMonitoring.statusSummary.total}</Heading>
            <Text>{t('TOTAL_EVENTS')}</Text>
          </Description>
        </ChartContainer>
      </ContentContainer>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
  width: 100%;
  min-width: 0;
  flex: 2;
`

const StyledDivider = styled(Divider)`
  margin: 0;
`

const ChartContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`

const Chart = styled.div`
  z-index: 1;
`

const Description = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`

const EventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: 8px;
  width: 100%;
`

const ContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 48px;
  margin: 24px 0;
`

const LightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export default StatusWidget
